import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeMotionRoundedIcon from "@mui/icons-material/AutoAwesomeMotionRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import PlaylistPlayRoundedIcon from "@mui/icons-material/PlaylistPlayRounded";
import { ownerApi } from "../../../../api/owner";
import { dateTime, surfaceSx } from "./ownerCenterUi";

const DESTINATIONS = [
  { key: "commitment", label: "Acción / compromiso", icon: <AddTaskRoundedIcon fontSize="small" /> },
  { key: "reminder", label: "Recordatorio", icon: <NotificationsActiveRoundedIcon fontSize="small" /> },
  { key: "initiative", label: "Proyecto / frente", icon: <FolderSpecialRoundedIcon fontSize="small" /> },
  { key: "interaction", label: "Contacto / interacción", icon: <ForumRoundedIcon fontSize="small" /> },
];

export default function OwnerInboxPanel({ entries = [], onChanged, onConvert, onProcess }) {
  const [menu, setMenu] = React.useState(null);

  const changeStatus = async (entry, status) => {
    await ownerApi.updateLifeEntry(entry.id || entry._id, status === "resolved"
      ? { status, resolutionNote: "Resuelto manualmente desde Inbox Owner", resolutionEntityType: "manual" }
      : { status });
    onChanged?.();
  };

  const openMenu = (event, entry) => setMenu({ anchorEl: event.currentTarget, entry });
  const closeMenu = () => setMenu(null);

  const chooseDestination = (destination) => {
    const entry = menu?.entry;
    closeMenu();
    if (entry) onConvert?.(entry, destination);
  };

  return (
    <Box sx={{ ...surfaceSx, p: 2, minHeight: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 1000 }}>Inbox</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 750 }}>
            Capturá primero. Convertí después sólo lo que necesite estructura.
          </Typography>
        </Box>
        <Stack direction="row" spacing={.55} alignItems="center">
          {entries.length ? (
            <Button
              size="small"
              startIcon={<PlaylistPlayRoundedIcon />}
              onClick={onProcess}
              sx={{ minHeight: 30, textTransform: "none", fontWeight: 950, px: .8 }}
            >
              Procesar
            </Button>
          ) : null}
          <Chip size="small" label={entries.length} sx={{ fontWeight: 950 }} />
        </Stack>
      </Stack>
      <Divider sx={{ mb: .6 }} />

      {entries.length ? entries.slice(0, 10).map((entry) => (
        <Box
          key={entry.id || entry._id}
          sx={{
            py: 1.05,
            borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)",
            "&:last-of-type": { borderBottom: 0 },
          }}
        >
          <Typography sx={{ fontSize: ".86rem", fontWeight: 850, whiteSpace: "pre-wrap" }}>{entry.text}</Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={.7}
            sx={{ mt: .55 }}
          >
            <Stack direction="row" spacing={.55} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                {dateTime(entry.occurredAt)} · {entry.inputSource}
              </Typography>
              {entry?.structuredData?.captureInterpretation?.retrospective ? <Chip size="small" label="Hecho pasado" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : entry?.structuredData?.captureInterpretation?.hasSuggestions ? <Chip size="small" label="Propuesta" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : null}
            </Stack>
            <Stack direction="row" spacing={.25} justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<AutoAwesomeMotionRoundedIcon />}
                onClick={(event) => openMenu(event, entry)}
                sx={{ minWidth: 0, textTransform: "none", fontWeight: 900 }}
              >
                Convertir
              </Button>
              <Tooltip title="Resolver sin crear otra entidad">
                <IconButton size="small" onClick={() => changeStatus(entry, "resolved")} aria-label="Resolver entrada">
                  <TaskAltRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Descartar del Inbox">
                <IconButton size="small" onClick={() => changeStatus(entry, "dismissed")} aria-label="Descartar entrada">
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      )) : (
        <Typography sx={{ py: 2, color: "text.secondary", fontSize: ".88rem" }}>
          Inbox limpio. Todo lo capturado ya fue atendido.
        </Typography>
      )}

      <Menu anchorEl={menu?.anchorEl || null} open={Boolean(menu)} onClose={closeMenu}>
        {DESTINATIONS.map((item) => (
          <MenuItem key={item.key} onClick={() => chooseDestination(item.key)}>
            <Stack direction="row" spacing={1} alignItems="center">{item.icon}<span>{item.label}</span></Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
